import { useEffect, useRef, useState, useCallback } from "react";
import { adminAuth } from "./identity";
import { api } from "./api";
import { notificationAudio } from "./notificationSound";
import { ccBus } from "./commandCenterBus";
import { settingsCache } from "./settingsCache";

/**
 * `useAdminFirehose` — single source of truth for the admin real-time
 * notification system.
 *
 * Mounted ONCE at the top of `AdminPanelPage` (the WS connection is
 * shared across all tabs). Exposes:
 *
 *   • `live`         — boolean: WS is OPEN
 *   • `unacked`      — array of {id, type, lat, lng, color, createdAt, flashUntil}
 *                       used by the badge counter + map flash layer
 *   • `flashing`     — array of alert ids still inside their 30 s flash window
 *                       OR not yet acknowledged. The map layer pulses based on
 *                       this list.
 *   • `ackAlert(id)` — POSTs /api/admin/alerts/{id}/ack + removes locally.
 *                       The server fans the ack out so OTHER admins drop the
 *                       alert from THEIR list too.
 *   • `clearLocal()` — drop everything from the in-memory unacked store
 *                       (used by the "tout marquer lu" button).
 *
 * The WS auto-reconnects with exponential backoff (1 → 30 s, capped) so a
 * dispatcher leaving the tab open overnight never silently loses live
 * alerts.
 */

const FLASH_DURATION_MS = 30_000;

export function useAdminFirehose({ enabled = true, onAlertCreated, onAlertAck } = {}) {
  const [live, setLive] = useState(false);
  const [unacked, setUnacked] = useState([]);   // newest first
  const wsRef = useRef(null);
  const retryRef = useRef(0);
  const timerRef = useRef(null);
  const closedRef = useRef(false);
  const cbCreatedRef = useRef(onAlertCreated);
  const cbAckRef = useRef(onAlertAck);
  // Keep the callback refs pointing at the latest props — done inside an
  // effect so the render body stays pure (react-hooks/refs forbids ref
  // writes during render).
  useEffect(() => { cbCreatedRef.current = onAlertCreated; }, [onAlertCreated]);
  useEffect(() => { cbAckRef.current = onAlertAck; }, [onAlertAck]);

  // ────────────────────────────────────────────────────────────────
  // Local message handlers (declared BEFORE the WS effect so the
  // onmessage closure doesn't sit in the TDZ at lint time).
  // ────────────────────────────────────────────────────────────────
  const handleAlertCreated = useCallback((alert) => {
    // Add to the unacked list if it isn't already there; play the sound.
    setUnacked((prev) => {
      if (prev.some((a) => a.id === alert.id)) return prev;
      const next = [{
        ...alert,
        flashUntil: Date.now() + FLASH_DURATION_MS,
      }, ...prev];
      // Cap the in-memory list at 200 alerts so a long-running tab doesn't OOM.
      return next.slice(0, 200);
    });
    // Honour the global "admin_sound_enabled" kill-switch (Phase 6).
    // The cache is hydrated by AdminPanelPage on mount and refreshed
    // after every successful Settings save.
    if (settingsCache.get("admin_sound_enabled")) {
      try { notificationAudio.playAdminAlert(); }
      catch (e) { console.warn("[useAdminFirehose] alert audio cue failed:", e?.message || e); }
    }
    if (cbCreatedRef.current) {
      try { cbCreatedRef.current(alert); } catch (e) { void e; }
    }
  }, []);

  const handleAlertAck = useCallback((alertId) => {
    setUnacked((prev) => prev.filter((a) => a.id !== alertId));
    if (cbAckRef.current) {
      try { cbAckRef.current(alertId); } catch (e) { void e; }
    }
  }, []);

  // ────────────────────────────────────────────────────────────────
  // WebSocket connection
  // ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!enabled) return undefined;
    if (!adminAuth.isLoggedIn()) return undefined;
    closedRef.current = false;

    // The browser auto-attaches the rr_admin_token httpOnly cookie when
    // opening a same-origin WebSocket. We just point at the endpoint.
    const buildUrl = () => {
      const base = process.env.REACT_APP_BACKEND_URL || "";
      const wsBase = base.replace(/^http/i, "ws");
      return `${wsBase}/api/ws/admin/firehose`;
    };

    const open = () => {
      if (closedRef.current) return;
      let ws;
      try { ws = new WebSocket(buildUrl()); }
      catch (_e) { schedule(); return; }
      wsRef.current = ws;
      ws.onopen = () => {
        setLive(true);
        retryRef.current = 0;
        ccBus.emit("ws.live", { live: true });
      };
      ws.onmessage = (ev) => {
        let msg;
        try { msg = JSON.parse(ev.data); }
        catch { return; }
        if (!msg || !msg.type) return;
        if (msg.type === "alert.created" && msg.alert) {
          handleAlertCreated(msg.alert);
        } else if (msg.type === "alert.acknowledged" && msg.alert_id) {
          handleAlertAck(msg.alert_id);
        }
        // Re-broadcast every recognized firehose event onto the local
        // bus so feature tabs (CommandCenterTab today, others later)
        // can react without owning a second WS connection.
        ccBus.emit(msg.type, msg);
      };
      ws.onclose = () => {
        setLive(false);
        ccBus.emit("ws.live", { live: false });
        if (!closedRef.current) schedule();
      };
      ws.onerror = () => {
        try { ws.close(); }
        catch (e) { console.warn("[useAdminFirehose] ws.close on error failed:", e?.message || e); }
      };
    };

    const schedule = () => {
      const delay = Math.min(1000 * Math.pow(2, retryRef.current), 30000);
      retryRef.current += 1;
      timerRef.current = setTimeout(open, delay);
    };

    open();

    return () => {
      closedRef.current = true;
      if (timerRef.current) clearTimeout(timerRef.current);
      if (wsRef.current) {
        try { wsRef.current.close(); }
        catch (e) { console.warn("[useAdminFirehose] ws cleanup close failed:", e?.message || e); }
      }
    };
  }, [enabled, handleAlertCreated, handleAlertAck]);

  // ────────────────────────────────────────────────────────────────
  // Public API
  // ────────────────────────────────────────────────────────────────
  const ackAlert = useCallback(async (alertId) => {
    // Optimistic local removal so the badge clears instantly even on a
    // 200 ms WS round-trip. The server will broadcast back to confirm
    // (idempotent — no-op if already removed).
    setUnacked((prev) => prev.filter((a) => a.id !== alertId));
    try {
      // Cookies + CSRF header are added by the axios interceptor.
      await api.post(`/admin/alerts/${encodeURIComponent(alertId)}/ack`);
    } catch (e) { void e; }
  }, []);

  const clearLocal = useCallback(() => setUnacked([]), []);

  return { live, unacked, ackAlert, clearLocal };
}
